import { createContext, type ReactNode, useContext, useState } from "react";

/** Supported viewer engines */
export type ViewerType = "babylon" | "three.js" | "model-viewer";

interface ViewerContextType {
  viewerType: ViewerType;
  setViewerType: (type: ViewerType) => void;
}

const ViewerContext = createContext<ViewerContextType>({
  setViewerType: () => {},
  viewerType: "babylon",
});

const STORAGE_KEY = "viewer-type";
const VALID_TYPES: ReadonlySet<ViewerType> = new Set([
  "babylon",
  "three.js",
  "model-viewer",
]);

/**
 * Resolve initial viewer type from URL param or localStorage.
 * URL `?viewer=` takes priority and is persisted to localStorage.
 */
const getInitialViewerType = (): ViewerType => {
  if (typeof window === "undefined") return "babylon";

  const params = new URLSearchParams(window.location.search);
  const urlValue = params.get("viewer");

  if (urlValue !== null) {
    if (VALID_TYPES.has(urlValue as ViewerType)) {
      localStorage.setItem(STORAGE_KEY, urlValue);
      return urlValue as ViewerType;
    }

    localStorage.removeItem(STORAGE_KEY);
    return "babylon";
  }

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && VALID_TYPES.has(stored as ViewerType)) {
    return stored as ViewerType;
  }

  return "babylon";
};

export const ViewerProvider = ({ children }: { children: ReactNode }) => {
  const [viewerType, setViewerTypeState] =
    useState<ViewerType>(getInitialViewerType);

  const setViewerType = (type: ViewerType) => {
    setViewerTypeState(type);
    localStorage.setItem(STORAGE_KEY, type);
  };

  return (
    <ViewerContext value={{ setViewerType, viewerType }}>
      {children}
    </ViewerContext>
  );
};

export const useViewer = (): ViewerContextType => {
  const context = useContext(ViewerContext);
  if (context === undefined) {
    throw new Error("useViewer must be used within a ViewerProvider");
  }
  return context;
};
